<div class="requests-container">

  <!-- Pending requests section -->
  <div class="section-header">
    <mat-icon class="section-icon pending-icon">notification_important</mat-icon>
    <span class="section-title">Pending Requests</span>
    <span class="count-badge" *ngIf="pendingRequests.length > 0">{{ pendingRequests.length }}</span>
  </div>

  <div *ngIf="pendingRequests.length === 0" class="empty-state">
    <mat-icon class="empty-icon">check_circle</mat-icon>
    <span>No pending re-share requests.</span>
  </div>

  <div class="request-list" *ngIf="pendingRequests.length > 0">
    <div *ngFor="let share of pendingRequests" class="request-card request-card--pending">

      <div class="request-card__header">
        <div class="request-card__meta">
          <span class="request-card__patient">
            <ng-container *ngIf="formatPatientName(share.patient_name) as name; else caseNameFallback">
              {{ name }}
              <span *ngIf="share.patient_id" class="request-card__patient-id"> · ID: {{ share.patient_id }}</span>
            </ng-container>
            <ng-template #caseNameFallback>
              <span class="request-card__patient-id">Ref: {{ share.case_name }}</span>
            </ng-template>
          </span>
          <span class="request-card__study-meta" *ngIf="share.study_date || share.accession_number">
            <span *ngIf="share.study_date">{{ share.study_date }}</span>
            <span *ngIf="share.study_date && share.accession_number"> · </span>
            <span *ngIf="share.accession_number">Acc: {{ share.accession_number }}</span>
          </span>
          <span class="request-card__facility">Spoke: {{ share.shared_to_facility_id }}</span>
          <span class="request-card__dates">
            Requested {{ formatDate(share.re_request_sent_at) }}
            <span *ngIf="share.re_request_count > 1"> · {{ share.re_request_count }} total requests</span>
          </span>
        </div>
      </div>

      <div class="request-card__actions">
        <button
          mat-flat-button
          color="primary"
          class="action-button"
          [disabled]="approvingIds.has(share.id) || rejectingIds.has(share.id)"
          (click)="onApprove(share)">
          <mat-progress-spinner
            *ngIf="approvingIds.has(share.id)"
            diameter="16"
            mode="indeterminate"
            class="button-spinner">
          </mat-progress-spinner>
          <mat-icon *ngIf="!approvingIds.has(share.id)">check_circle</mat-icon>
          <span>Approve</span>
        </button>

        <button
          mat-stroked-button
          color="warn"
          class="action-button"
          [disabled]="rejectingIds.has(share.id) || approvingIds.has(share.id)"
          (click)="onReject(share)">
          <mat-progress-spinner
            *ngIf="rejectingIds.has(share.id)"
            diameter="16"
            mode="indeterminate"
            class="button-spinner">
          </mat-progress-spinner>
          <mat-icon *ngIf="!rejectingIds.has(share.id)">cancel</mat-icon>
          <span>Reject</span>
        </button>

        <button mat-button class="details-button" (click)="toggleDetails(share.id)">
          <mat-icon>{{ expandedShareId === share.id ? 'expand_less' : 'info_outline' }}</mat-icon>
          {{ expandedShareId === share.id ? 'Hide' : 'Details' }}
        </button>
      </div>

      <div *ngIf="expandedShareId === share.id" class="request-card__details">
        <div class="detail-row">
          <span class="detail-label">Spoke facility</span>
          <span class="detail-value">{{ share.shared_to_facility_id }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Study UID</span>
          <span class="detail-value mono">{{ share.study_instance_uid }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Patient Ref</span>
          <span class="detail-value">{{ share.case_name }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Originally shared</span>
          <span class="detail-value">{{ formatDate(share.shared_at) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Expired on</span>
          <span class="detail-value">{{ formatDate(share.expired_at || share.effective_expires_at) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Requested on</span>
          <span class="detail-value">{{ formatDate(share.re_request_sent_at) }}</span>
        </div>
      </div>

    </div>
  </div>

  <!-- Request history section -->
  <div *ngIf="requestHistory.length > 0" class="history-section">
    <div class="section-header section-header--secondary">
      <mat-icon class="section-icon">history</mat-icon>
      <span class="section-title">Request History</span>
    </div>

    <div class="request-list">
      <div *ngFor="let share of requestHistory" class="request-card"
           [class.request-card--approved]="share.status === 'reshared'"
           [class.request-card--rejected]="share.status === 'rejected'">

        <div class="request-card__header">
          <div class="request-card__meta">
            <span class="request-card__patient">
              {{ share.patient_name || share.case_name }}
              <span *ngIf="share.patient_id" class="request-card__patient-id"> · {{ share.patient_id }}</span>
            </span>
            <span class="request-card__study-meta" *ngIf="share.study_date || share.accession_number">
              <span *ngIf="share.study_date">{{ share.study_date }}</span>
              <span *ngIf="share.study_date && share.accession_number"> · </span>
              <span *ngIf="share.accession_number">Acc: {{ share.accession_number }}</span>
            </span>
            <span class="request-card__facility">Spoke: {{ share.shared_to_facility_id }}</span>
            <span class="request-card__dates">
              Requested {{ formatDate(share.re_request_sent_at) }}
            </span>
          </div>
          <span class="status-chip" [class]="statusClass(share)">{{ statusLabel(share) }}</span>
        </div>

        <div class="request-card__actions">
          <button mat-button class="details-button" (click)="toggleDetails(share.id)">
            <mat-icon>{{ expandedShareId === share.id ? 'expand_less' : 'info_outline' }}</mat-icon>
            {{ expandedShareId === share.id ? 'Hide' : 'Details' }}
          </button>
        </div>

        <div *ngIf="expandedShareId === share.id" class="request-card__details">
          <div class="detail-row">
            <span class="detail-label">Spoke facility</span>
            <span class="detail-value">{{ share.shared_to_facility_id }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Study UID</span>
            <span class="detail-value mono">{{ share.study_instance_uid }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Patient Ref</span>
            <span class="detail-value">{{ share.case_name }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Originally shared</span>
            <span class="detail-value">{{ formatDate(share.shared_at) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Requested on</span>
            <span class="detail-value">{{ formatDate(share.re_request_sent_at) }}</span>
          </div>
        </div>

      </div>
    </div>
  </div>

</div>
